import { html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { SUBMIT_BUTTON_DEFAULT_PROPS as DEFAULT_PROPS } from "./button.defs";
import type {
  SbSubmitButtonProps as Props,
  SubmitButtonContent,
  SubmitButtonIconAlign,
  SubmitButtonPart,
  SubmitButtonSize,
  SubmitButtonVariant,
} from "./button.types";

/**
 * Button that submits an auth widget form, forwarding an `event` identifier for
 * the authentication service.
 *
 * @part submit-button - rtg-button: root
 * @part submit-button-icon - svg/rtg-spinner: icon or loading indicator
 *
 * @event sb-submit-button:click - Fired when the button is clicked.
 *  Detail: `{ id: string; event?: string }`
 */
@customElement("sb-submit-button")
export class SbSubmitButton extends LitElement implements Props {
  static readonly ROOT = "submit-button";
  static readonly ICON = "icon";

  static readonly TAG = `${NAMESPACE}-${SbSubmitButton.ROOT}`;

  static readonly CLICK_EVENT = `${SbSubmitButton.TAG}:click`;

  static readonly PARTS: Record<SubmitButtonPart, string> = {
    root: SbSubmitButton.ROOT,
    icon: `${SbSubmitButton.ROOT}-${SbSubmitButton.ICON}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Visual variant of the button component.
   */
  @property({ type: String })
  variant: SubmitButtonVariant = DEFAULT_PROPS.variant;

  /**
   * Size of the button component.
   */
  @property({ type: String })
  size: SubmitButtonSize = DEFAULT_PROPS.size;

  /**
   * Controls what the button renders: icon only, label only, or both.
   */
  @property({ type: String })
  content: SubmitButtonContent = DEFAULT_PROPS.content;

  /**
   * Side the icon appears on relative to the label.
   */
  @property({ type: String, attribute: "icon-align" })
  iconAlign: SubmitButtonIconAlign = DEFAULT_PROPS.iconAlign;

  /**
   * Text used as the label of the button component.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /**
   * Event identifier forwarded to the authentication service on click.
   * When omitted, only the custom click event is dispatched.
   */
  @property({ type: String })
  event?: string;

  /**
   * Disables the button, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Applies a loading state, disabling the button and displaying a spinner.
   */
  @property({ type: Boolean })
  loading?: boolean;

  /* ---------------------------------------- */

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbSubmitButton.TAG}-${this.seed}`;
  }

  get iconId() {
    return `${this.rootId}-${SbSubmitButton.ICON}`;
  }

  get isDisabled() {
    return this.disabled || this.loading;
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this._handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
  }

  /* ------------------------------------------------------------ */

  private _handleClick = () => {
    if (this.isDisabled) return;

    this.dispatchEvent(
      new CustomEvent(SbSubmitButton.CLICK_EVENT, {
        detail: { id: this.rootId, event: this.event },
        bubbles: true,
        composed: true,
      }),
    );
  };

  /* ------------------------------------------------------------ */

  private _renderIcon() {
    const cn =
      this.content === "icon" ? "" : `rtg-icon-align-${this.iconAlign}`;

    if (this.loading)
      return html`
        <rtg-spinner
          id="${this.iconId}"
          part="${SbSubmitButton.PARTS.icon}"
          class="${cn ?? nothing}"
        ></rtg-spinner>
      `;

    if (!this.content.includes("icon")) return nothing;

    return html`
      <svg
        id="${this.iconId}"
        part="${SbSubmitButton.PARTS.icon}"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-arrow-right-icon lucide-arrow-right ${cn}"
      >
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
      </svg>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-button
        id="${this.rootId}"
        part="${SbSubmitButton.PARTS.root}"
        data-event="${this.event ?? nothing}"
        data-loading="${this.loading ? "true" : nothing}"
        variant="${this.variant}"
        size="${this.size}"
        type="submit"
        ?disabled="${this.isDisabled}"
      >
        ${this.iconAlign === "start" ? this._renderIcon() : nothing}
        ${this.content.includes("label") ? this.label : nothing}
        ${this.iconAlign === "end" ? this._renderIcon() : nothing}
      </rtg-button>
    `;
  }
}
